'use client';

import { createContext, ReactNode, useContext } from "react";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType,
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import { Privacy, PrivacyInput } from "@/app/lib/generated/graphql-types";
import { PrivacyEditable } from "@/app/features/sites/privacy/types/types";
import { PrivacySchema } from "@/app/features/sites/privacy/types/schema";
import { getPrivacyPolicy } from "@/app/features/sites/privacy/data-access/queries";
import { updatePrivacyPolicy } from "@/app/features/sites/privacy/data-access/mutations";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { toggleDraftMode } from "@/app/lib/actions/editing";

const PrivacyEditorContext = createContext<UseSingleTypeEditorType<PrivacyEditable> | undefined>(undefined);

export function usePrivacyEditor() {
    const context = useContext(PrivacyEditorContext);

    if (!context) {
        throw new Error("usePrivacyEditor must be used within a PrivacyEditorProvider");
    }

    return context;
}

interface PrivacyEditorProviderProps {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}

export default function PrivacyEditorProvider({
    children,
    isDraftModeEnabled,
}: PrivacyEditorProviderProps) {
    const editor = useSingleTypeEditor<Privacy, PrivacyEditable, PrivacyInput>({
        schema: PrivacySchema,
        loadEntity: getPrivacyPolicy,
        update: updatePrivacyPolicy,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <PrivacyEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={editor.discardChanges}
            />
        </PrivacyEditorContext.Provider>
    );
}
